
/**
 * Vue Inline SVG
 * Component from https://github.com/shrpne/vue-inline-svg
*/
import { h as createElement } from 'vue';
import {Options, Vue} from "vue-class-component";

const cache: any = {};

/**
 * Remove false attrs
 * @param {Object} attrs
 */

function filterAttrs(attrs:any) {
    return Object.keys(attrs).reduce(function (result:any, key) {
    if (attrs[key] !== false && attrs[key] !== null && attrs[key] !== undefined) {
        result[key] = attrs[key];
    }

    return result;
    }, {});
}

@Options({
    name: 'InlineSvg',
    inheritAttrs: false,
    render() {
        if (!this.svgElSource) {
            return null;
        }
        return createElement(
            'svg',
            Object.assign(
                {},
                // source attrs
                this.getSvgAttrs(this.svgElSource),
                // component attrs and listeners
                filterAttrs(this.$attrs),
                // content
                { innerHTML: this.getSvgContent(this.svgElSource) },
            ),
        );
    },
    props: {
        src: {
            type: String,
            required: true,
        },
        title: {
            type: String,
        },
        transformSource: {
            type: Function,
            default: (svg:any) => svg,
        },
        keepDuringLoading: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['loaded', 'unloaded', 'error'],
    data() {
        return {
            /** @type SVGElement */
            svgElSource: null,
        };
    },
    watch: {
        src(newValue) {
            // re-generate cached svg (`svgElSource`)
            this.getSource(newValue);
        },
    },
    mounted() {
        // generate `svgElSource`
        this.getSource(this.src);
    },
    methods: {
        getSvgAttrs(svgEl:any) {
            // copy attrs
            const svgAttrs:any = {};
            const attrs = svgEl.attributes;
            if (!attrs) {
                return svgAttrs;
            }
            for (let i = attrs.length - 1; i >= 0; i--) {
                svgAttrs[attrs[i].name] = attrs[i].value;
            }
            return svgAttrs;
        },
        getSvgContent(svgEl:any) {
            svgEl = svgEl.cloneNode(true);
            svgEl = this.transformSource(svgEl);
            if (this.title) {
                setTitle(svgEl, this.title);
            }

            // copy inner html
            return svgEl.innerHTML;
        },
        /**
         * Get svgElSource
         * @param {string} src
         */
        getSource(src:any) {
            // fill cache by src with promise
            if (!cache[src]) {
                // download
                cache[src] = this.download(src);
            }
            // notify svg is unloaded
            if (this.svgElSource && cache[src].isPending() && !this.keepDuringLoading) {
                this.svgElSource = null;
                this.$emit('unloaded');
            }

            // inline svg when cached promise resolves
            cache[src]
                .then((svg:any) => {
                    this.svgElSource = svg;
                    // wait to render
                    this.$nextTick(() => {
                        // notify
                        this.$emit('loaded', this.$el);
                    });
                })
                .catch((err:any) => {
                    // notify svg is unloaded
                    if (this.svgElSource) {
                        this.svgElSource = null;
                        this.$emit('unloaded');
                    }
                    // remove cached rejected promise so next image can try load again
                    delete cache[src];
                    this.$emit('error', err);
                });
        },

        /**
         * Get the contents of the SVG
         * @param {string} url
         * @returns {Promise<Element>}
         */
        download(url:any) {
            return makePromiseState(new Promise((resolve, reject) => {
                const request = new XMLHttpRequest();
                request.open('GET', url, true);

                request.onload = () => {
                    if (request.status >= 200 && request.status < 400) {
                        try {
                            // Setup a parser to convert the response to text/xml in order for it to be manipulated and changed
                            const parser = new DOMParser();
                            const result = parser.parseFromString(request.responseText, 'text/xml');
                            const svgEl = result.getElementsByTagName('svg')[0];
                            if (svgEl) {
                                // svgEl = this.transformSource(svgEl);
                                resolve(svgEl);
                            } else {
                                reject(new Error('Loaded file is not valid SVG"'));
                            }
                        } catch (e) {
                            reject(e);
                        }
                    } else {
                        reject(new Error('Error loading SVG'));
                    }
                };

                request.onerror = reject;
                request.send();
            }));
        },
    },
})
export default class InlineSvg extends Vue {}
/**
 * Create or edit the <title> element of a SVG
 * @param {SVGElement} svg
 * @param {string} title
 */

function setTitle(svg:any, title:any) {
  const titleTags = svg.getElementsByTagName('title');

  if (titleTags.length) {
    // overwrite existing title
    titleTags[0].textContent = title;
    } else {
    // create a title element if one doesn't already exist
    const titleEl = document.createElementNS('http://www.w3.org/2000/svg', 'title');
    titleEl.textContent = title;
    svg.appendChild(titleEl);
    }
}
/**
 * @typedef {Promise} PromiseWithState
 * @property {Function<boolean>} isPending
 */

/**
 * This function allow you to modify a JS Promise by adding some status properties.
 * @param {Promise|PromiseWithState} promise
 * @return {PromiseWithState}
 */


function makePromiseState(promise:any) {
    // Don't modify any promise that has been already modified.
    if (promise.isPending) return promise; // Set initial state

  let isPending = true; // Observe the promise, saving the fulfillment in a closure scope.

  const result = promise.then(function (v: any) {
    isPending = false;
    return v;
  }, function (e: any) {
    isPending = false;
    throw e;
  });

  result.isPending = function getIsPending() {
    return isPending;
    };

    return result;
}


